/**
 * ============================================================================
 * CapaDetail — Phase 7 frontend (CAPA Module).
 * Owner: Phase 7 — CAPA Module agent.
 * Route: /capa/:capaId
 * Purpose: per-CAPA workspace with FSM timeline, action-step table, signature
 *          roster, and the effectiveness-verification panel (CAPA-007).
 *
 * Layout: 2-col grid:
 *   Main column   — title + status pill + transition controls + steps table.
 *   Side column   — timeline (CAPA-001 append-only) + effectiveness panel
 *                   + signature roster.
 *
 * URS rows: CAPA-001..010 + CAPA-005 + CAPA-007.
 * ============================================================================ */

import { useEffect, useState, useCallback } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './CapaWorkbench.module.css';
import { CostAttributionPanel } from '../Finance/CostAttribution';
import { RelatedRecords } from '../../components/RelatedRecords';
import { CapaDepthPanel, type EffectivenessCheck } from './CapaDepthPanel';
import type {
  CapaStatus,
  CapaCriticality,
  CapaSourceKind,
  CapaType,
} from './useCapasQuery';

type EffectivenessOutcome = 'EFFECTIVE' | 'INEFFECTIVE' | 'PARTIAL';
type SignatureMeaning = 'AUTHORSHIP' | 'APPROVAL' | 'EFFECTIVENESS_VERIFICATION';

interface TimelineEvent {
  historyId: string;
  eventCode: string;
  eventAtUtc: string;
  actorUserId: string;
  payload: Record<string, unknown>;
}

interface ActionStep {
  stepId: string;
  capaId: string;
  sequenceNo: number;
  description: string;
  ownerUserId: string;
  dueUtc: string;
  completedUtc: string | null;
  completionEvidenceDocId: string | null;
  completionSignatureId: string | null;
  createsTrainingTask: boolean;
  createsCc: boolean;
  isComplete: boolean;
}

interface SignatureRow {
  signatureId: string;
  meaning: SignatureMeaning;
  signerUserId: string;
  signedAtUtc: string;
  attestationVersion: number;
}

interface CapaDetailDoc {
  capaId: string;
  capaNumber: string;
  title: string;
  description: string;
  capaType: CapaType;
  sourceKind: CapaSourceKind;
  sourceRef: string | null;
  ownerUserId: string;
  criticality: CapaCriticality;
  status: CapaStatus;
  dueDateUtc: string;
  targetCloseUtc: string | null;
  closedAtUtc: string | null;
  effectivenessCheckSignatureId: string | null;
  effectivenessOutcome: EffectivenessOutcome | null;
  rationaleOnClose: string | null;
  createdAtUtc: string;
  createdByUserId: string;
  steps: ActionStep[];
  timeline: TimelineEvent[];
  signatures: SignatureRow[];
  // C-10 — root-cause taxonomy + 30/90/180-day effectiveness trend.
  rootCauseCategory?: string | null;
  rootCauseDetail?: string | null;
  effectivenessChecks?: EffectivenessCheck[];
}

export interface CapaDetailProps {
  capaId: string;
  onBack?: () => void;
}

export function CapaDetail({ capaId, onBack }: CapaDetailProps) {
  const { t, i18n } = useTranslation();
  const [doc, setDoc] = useState<CapaDetailDoc>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetcher = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const r = await fetch(apiUrl(`/api/v1/capa/${encodeURIComponent(capaId)}`), { headers: buildHeaders() });
      if (!r.ok) throw new Error(`GET capa ${r.status}`);
      const body = (await r.json()) as CapaDetailDoc;
      setDoc(body);
    } catch (e) {
      setIsError(true);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [capaId]);

  useEffect(() => {
    fetcher();
  }, [fetcher]);

  const df = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  // Defensive date format — a null/invalid date must not crash the screen
  // (real records can have an open step with no due date, etc.).
  const fmtDate = (v?: string | null) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : df.format(d);
  };

  if (isLoading) {
    return (
      <div className={wstyles.adminScreen}>
        <div className={styles.skeletonRow} />
        <div className={styles.skeletonRow} />
        <div className={styles.skeletonRow} />
      </div>
    );
  }

  if (isError || !doc) {
    return (
      <div className={wstyles.adminScreen}>
        <div className={styles.errorBanner} role="alert">
          <span>
            {t('capa.detail.error', {
              defaultValue: 'Could not load CAPA detail.',
            })}
          </span>
          <Button variant="secondary" onClick={fetcher}>
            {t('common.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={wstyles.adminScreen}>
      {onBack && (
        <button
          type="button"
          className={styles.linkBtn}
          onClick={onBack}
          aria-label={t('common.back', { defaultValue: 'Back' })}
        >
          ← {t('common.back', { defaultValue: 'Back' })}
        </button>
      )}
      <PageHero
        eyebrow={t('capa.detail.eyebrow', { defaultValue: 'Quality Events' })}
        title={
          <>
            {doc.capaNumber}{' '}
            <span className={styles.muted}>— {doc.title}</span>
          </>
        }
        subtitle={doc.description}
      />

      <div className={styles.detailGrid}>
        <div className={styles.detailMain}>
          {/* ---------------- Header card ---------------- */}
          <Card>
            <CardHeader>
              <h2 className={styles.sectionTitle}>
                {t('capa.detail.summary', { defaultValue: 'Summary' })}
              </h2>
            </CardHeader>
            <CardBody>
              <dl className={styles.kvList}>
                <dt>{t('capa.detail.status', { defaultValue: 'Status' })}</dt>
                <dd>
                  <StatusPill status={doc.status} />
                </dd>
                <dt>
                  {t('capa.detail.criticality', { defaultValue: 'Criticality' })}
                </dt>
                <dd>
                  <CriticalityPill c={doc.criticality} />
                </dd>
                <dt>{t('capa.detail.type', { defaultValue: 'Type' })}</dt>
                <dd>{doc.capaType}</dd>
                <dt>
                  {t('capa.detail.source', { defaultValue: 'Source' })}
                </dt>
                <dd>
                  {doc.sourceKind}
                  {doc.sourceRef ? ` / ${doc.sourceRef}` : ''}
                </dd>
                <dt>{t('capa.detail.owner', { defaultValue: 'Owner' })}</dt>
                <dd>{doc.ownerUserId}</dd>
                <dt>{t('capa.detail.due', { defaultValue: 'Due' })}</dt>
                <dd>{fmtDate(doc.dueDateUtc)}</dd>
                {doc.closedAtUtc && (
                  <>
                    <dt>
                      {t('capa.detail.closedAt', {
                        defaultValue: 'Closed at',
                      })}
                    </dt>
                    <dd>{fmtDate(doc.closedAtUtc)}</dd>
                  </>
                )}
              </dl>
            </CardBody>
          </Card>

          {/* ---------------- FSM transition controls (CAPA-001) ---------------- */}
          <TransitionControls doc={doc} onTransitioned={fetcher} />

          {/* ---------------- Steps ---------------- */}
          <Card>
            <CardHeader>
              <h2 className={styles.sectionTitle}>
                {t('capa.detail.steps', { defaultValue: 'Action steps' })}
              </h2>
            </CardHeader>
            <CardBody>
              {doc.steps.length === 0 ? (
                <p className={styles.muted}>
                  {t('capa.detail.noSteps', {
                    defaultValue: 'No steps yet.',
                  })}
                </p>
              ) : (
                <table className={styles.stepsTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>
                        {t('capa.detail.col.desc', {
                          defaultValue: 'Description',
                        })}
                      </th>
                      <th>
                        {t('capa.detail.col.owner', {
                          defaultValue: 'Owner',
                        })}
                      </th>
                      <th>
                        {t('capa.detail.col.due', { defaultValue: 'Due' })}
                      </th>
                      <th>
                        {t('capa.detail.col.flags', {
                          defaultValue: 'Cascades',
                        })}
                      </th>
                      <th>
                        {t('capa.detail.col.status', {
                          defaultValue: 'Status',
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.steps.map((s) => (
                      <tr key={s.stepId}>
                        <td>{s.sequenceNo}</td>
                        <td>{s.description}</td>
                        <td>{s.ownerUserId}</td>
                        <td>{fmtDate(s.dueUtc)}</td>
                        <td>
                          {s.createsTrainingTask && (
                            <span className={styles.chip}>TRN</span>
                          )}{' '}
                          {s.createsCc && (
                            <span className={styles.chip}>CC</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`${styles.chip} ${
                              s.isComplete ? styles.stepDone : styles.stepPending
                            }`}
                          >
                            {s.isComplete ? 'DONE' : 'PENDING'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>

          {/* ---------------- Effectiveness panel ---------------- */}
          <EffectivenessPanel
            doc={doc}
            onVerified={fetcher}
          />
        </div>

        <div className={styles.detailSide}>
          {/* ---------------- Timeline ---------------- */}
          <Card>
            <CardHeader>
              <h2 className={styles.sectionTitle}>
                {t('capa.detail.timeline', { defaultValue: 'Timeline' })}
              </h2>
            </CardHeader>
            <CardBody>
              {doc.timeline.length === 0 ? (
                <p className={styles.muted}>
                  {t('capa.detail.noEvents', {
                    defaultValue: 'No events yet.',
                  })}
                </p>
              ) : (
                <ul className={styles.timeline}>
                  {doc.timeline.map((e) => (
                    <li key={e.historyId} className={styles.timelineItem}>
                      <span className={styles.timelineTime}>
                        {fmtDate(e.eventAtUtc)}
                      </span>
                      <div>
                        <div className={styles.timelineEvent}>{e.eventCode}</div>
                        <div className={styles.timelineActor}>
                          {t('capa.detail.actor', {
                            defaultValue: 'actor',
                          })}
                          : {e.actorUserId}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* ---------------- Signatures ---------------- */}
          <Card>
            <CardHeader>
              <h2 className={styles.sectionTitle}>
                {t('capa.detail.signatures', { defaultValue: 'Signatures' })}
              </h2>
            </CardHeader>
            <CardBody>
              {doc.signatures.length === 0 ? (
                <p className={styles.muted}>
                  {t('capa.detail.noSigs', {
                    defaultValue: 'No signatures yet.',
                  })}
                </p>
              ) : (
                <ul className={styles.timeline}>
                  {doc.signatures.map((s) => (
                    <li key={s.signatureId} className={styles.timelineItem}>
                      <span className={styles.timelineTime}>
                        {fmtDate(s.signedAtUtc)}
                      </span>
                      <div>
                        <div className={styles.timelineEvent}>{s.meaning}</div>
                        <div className={styles.timelineActor}>
                          {s.signerUserId} v{s.attestationVersion}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* C-10 — root-cause taxonomy + effectiveness trend. */}
          <CapaDepthPanel
            capaId={doc.capaId}
            rootCauseCategory={doc.rootCauseCategory ?? null}
            rootCauseDetail={doc.rootCauseDetail ?? null}
            effectivenessChecks={doc.effectivenessChecks ?? []}
            onChanged={fetcher}
          />

          {/* C-4 — cross-module Related Records (event-linking engine). */}
          <RelatedRecords entityType="CAPA" entityId={doc.capaId} />

          {/* Finance module — Cost Attribution (v0.13 Tier 1.2).
              Role-gated; hidden for non-finance users. */}
          <CostAttributionPanel
            subjectEntityType="CAPA"
            subjectEntityId={doc.capaId}
            subjectDisplayLabel={`${doc.capaNumber} — ${doc.title}`}
          />
        </div>
      </div>
    </div>
  );
}

// CAPA-001 lifecycle — mirrors the server gate (CAPA_ALLOWED_NEXT in
// pgHandlers.mjs). CLOSED is intentionally omitted: closure happens through the
// effectiveness panel (POST /capa/{id}/effectiveness), not a bare transition.
interface CapaMove { target: CapaStatus; labelKey: string; defaultLabel: string; forward: boolean }
const CAPA_TRANSITIONS: Record<CapaStatus, CapaMove[]> = {
  IDENTIFIED: [
    { target: 'INVESTIGATION', labelKey: 'capa.fsm.investigate', defaultLabel: 'Start investigation', forward: true },
  ],
  INVESTIGATION: [
    { target: 'ACTION_PLAN', labelKey: 'capa.fsm.plan', defaultLabel: 'Draft action plan', forward: true },
    { target: 'IDENTIFIED', labelKey: 'capa.fsm.backIdentified', defaultLabel: 'Back to identified', forward: false },
  ],
  ACTION_PLAN: [
    { target: 'IMPLEMENTATION', labelKey: 'capa.fsm.implement', defaultLabel: 'Begin implementation', forward: true },
    { target: 'INVESTIGATION', labelKey: 'capa.fsm.backInvestigation', defaultLabel: 'Back to investigation', forward: false },
  ],
  IMPLEMENTATION: [
    { target: 'EFFECTIVENESS_CHECK', labelKey: 'capa.fsm.toEffCheck', defaultLabel: 'Move to effectiveness check', forward: true },
    { target: 'ACTION_PLAN', labelKey: 'capa.fsm.backActionPlan', defaultLabel: 'Back to action plan', forward: false },
  ],
  EFFECTIVENESS_CHECK: [
    { target: 'IMPLEMENTATION', labelKey: 'capa.fsm.backImplementation', defaultLabel: 'Back to implementation', forward: false },
  ],
  CLOSED: [],
  CANCELLED: [],
};

interface TransitionControlsProps {
  doc: CapaDetailDoc;
  onTransitioned: () => void;
}

function TransitionControls({ doc, onTransitioned }: TransitionControlsProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string>();
  const [err, setErr] = useState<string>();
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const moves = CAPA_TRANSITIONS[doc.status] ?? [];
  const canCancel = doc.status !== 'CLOSED' && doc.status !== 'CANCELLED';
  if (moves.length === 0 && !canCancel) return null; // terminal state

  async function go(target: CapaStatus) {
    setBusy(target);
    setErr(undefined);
    try {
      const r = await fetch(
        apiUrl(`/api/v1/capa/${encodeURIComponent(doc.capaId)}/transition`),
        {
          method: 'POST',
          headers: buildHeaders({ json: true }),
          body: JSON.stringify({ targetStatus: target }),
        },
      );
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        setErr((b as { detail?: string }).detail ?? t('capa.fsm.failed', { defaultValue: 'Transition failed.' }));
        return;
      }
      setConfirmingCancel(false);
      onTransitioned();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <Card>
      <CardHeader>
        <h2 className={styles.sectionTitle}>
          {t('capa.detail.workflow', { defaultValue: 'Advance workflow' })}
        </h2>
      </CardHeader>
      <CardBody>
        <p className={styles.muted} style={{ marginTop: 0 }}>
          {t('capa.fsm.help', {
            defaultValue:
              'CAPA-001 lifecycle. Closure is performed through the effectiveness panel below, not here.',
          })}
        </p>
        <div className={styles.formActions} style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {moves.map((m) => (
            <Button
              key={m.target}
              variant={m.forward ? 'primary' : 'secondary'}
              disabled={!!busy}
              onClick={() => go(m.target)}
            >
              {busy === m.target
                ? t('common.submitting', { defaultValue: 'Submitting…' })
                : t(m.labelKey, { defaultValue: m.defaultLabel })}
            </Button>
          ))}
          {canCancel && !confirmingCancel && (
            <Button variant="destructive" disabled={!!busy} onClick={() => setConfirmingCancel(true)}>
              {t('capa.fsm.cancel', { defaultValue: 'Cancel CAPA' })}
            </Button>
          )}
          {canCancel && confirmingCancel && (
            <>
              <Button variant="destructive" disabled={!!busy} onClick={() => go('CANCELLED')}>
                {busy === 'CANCELLED'
                  ? t('common.submitting', { defaultValue: 'Submitting…' })
                  : t('capa.fsm.confirmCancel', { defaultValue: 'Confirm cancel' })}
              </Button>
              <Button variant="secondary" disabled={!!busy} onClick={() => setConfirmingCancel(false)}>
                {t('capa.fsm.keepOpen', { defaultValue: 'Keep open' })}
              </Button>
            </>
          )}
        </div>
        {err && (
          <div className={styles.errorBanner} role="alert" style={{ marginTop: 'var(--space-3)' }}>
            {err}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function StatusPill({ status }: { status: CapaStatus }) {
  const cls =
    status === 'CLOSED'
      ? styles.statusClosed
      : status === 'CANCELLED'
        ? styles.statusCancelled
        : status === 'EFFECTIVENESS_CHECK'
          ? styles.statusEffectivenessCheck
          : status === 'IMPLEMENTATION'
            ? styles.statusImplementation
            : status === 'ACTION_PLAN'
              ? styles.statusActionPlan
              : status === 'INVESTIGATION'
                ? styles.statusInvestigation
                : styles.statusIdentified;
  return <span className={`${styles.chip} ${cls}`}>{status}</span>;
}

function CriticalityPill({ c }: { c: CapaCriticality }) {
  const cls =
    c === 'CRITICAL'
      ? styles.critCritical
      : c === 'MAJOR'
        ? styles.critMajor
        : styles.critMinor;
  return <span className={`${styles.chip} ${cls}`}>{c}</span>;
}

interface EffectivenessPanelProps {
  doc: CapaDetailDoc;
  onVerified: () => void;
}

function EffectivenessPanel({ doc, onVerified }: EffectivenessPanelProps) {
  const { t } = useTranslation();
  const [verifierUserId, setVerifierUserId] = useState('');
  const [outcome, setOutcome] = useState<EffectivenessOutcome>('EFFECTIVE');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  const canVerify = doc.status === 'EFFECTIVENESS_CHECK';

  const outcomeClass =
    doc.effectivenessOutcome === 'EFFECTIVE'
      ? styles.effEffective
      : doc.effectivenessOutcome === 'INEFFECTIVE'
        ? styles.effIneffective
        : doc.effectivenessOutcome === 'PARTIAL'
          ? styles.effPartial
          : styles.effNone;

  async function submit() {
    if (!verifierUserId.trim() || rationale.trim().length < 30) {
      setErrorMsg(
        t('capa.eff.minRationale', {
          defaultValue:
            'Verifier and a rationale ≥ 30 characters are required (CAPA-007).',
        }),
      );
      return;
    }
    setSubmitting(true);
    setErrorMsg(undefined);
    try {
      const r = await fetch(
        apiUrl(`/api/v1/capa/${encodeURIComponent(doc.capaId)}/effectiveness`),
        {
          method: 'POST',
          headers: buildHeaders({ json: true }),
          body: JSON.stringify({
            verifierUserId: verifierUserId.trim(),
            outcome,
            rationale: rationale.trim(),
            attestationVersion: 1,
          }),
        },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMsg(
          (body as { detail?: string }).detail ??
            t('capa.eff.failed', {
              defaultValue: 'Effectiveness verification failed.',
            }),
        );
        return;
      }
      onVerified();
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <h2 className={styles.sectionTitle}>
          {t('capa.detail.effPanel', {
            defaultValue: 'Effectiveness verification',
          })}
        </h2>
      </CardHeader>
      <CardBody>
        <div className={styles.effPanel}>
          <dl className={styles.kvList}>
            <dt>
              {t('capa.detail.effOutcome', { defaultValue: 'Outcome' })}
            </dt>
            <dd>
              <span className={`${styles.effOutcomeBadge} ${outcomeClass}`}>
                {doc.effectivenessOutcome ?? 'PENDING'}
              </span>
            </dd>
            {doc.effectivenessCheckSignatureId && (
              <>
                <dt>
                  {t('capa.detail.effSig', { defaultValue: 'Signature' })}
                </dt>
                <dd>{doc.effectivenessCheckSignatureId}</dd>
              </>
            )}
            {doc.rationaleOnClose && (
              <>
                <dt>
                  {t('capa.detail.effRationale', {
                    defaultValue: 'Rationale',
                  })}
                </dt>
                <dd>{doc.rationaleOnClose}</dd>
              </>
            )}
          </dl>

          {canVerify && (
            <>
              <div className={styles.formField}>
                <label htmlFor="ver-user">
                  {t('capa.detail.verifierLabel', {
                    defaultValue: 'Verifier user id (CAPA-005 SoD)',
                  })}
                </label>
                <input
                  id="ver-user"
                  value={verifierUserId}
                  onChange={(e) => setVerifierUserId(e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <label htmlFor="ver-outcome">
                  {t('capa.detail.outcomeLabel', {
                    defaultValue: 'Outcome',
                  })}
                </label>
                <select
                  id="ver-outcome"
                  value={outcome}
                  onChange={(e) =>
                    setOutcome(e.target.value as EffectivenessOutcome)
                  }
                >
                  <option value="EFFECTIVE">EFFECTIVE (closes CAPA)</option>
                  <option value="PARTIAL">PARTIAL (backtrack)</option>
                  <option value="INEFFECTIVE">
                    INEFFECTIVE (backtrack)
                  </option>
                </select>
              </div>
              <div className={styles.formField}>
                <label htmlFor="ver-rationale">
                  {t('capa.detail.rationaleLabel', {
                    defaultValue: 'Rationale (≥ 30 chars)',
                  })}
                </label>
                <textarea
                  id="ver-rationale"
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                />
              </div>
              {errorMsg && (
                <div className={styles.errorBanner} role="alert">
                  {errorMsg}
                </div>
              )}
              <div className={styles.formActions}>
                <Button
                  variant="primary"
                  onClick={submit}
                  disabled={submitting}
                >
                  {submitting
                    ? t('common.submitting', { defaultValue: 'Submitting…' })
                    : t('capa.detail.verify', {
                        defaultValue: 'Submit verification',
                      })}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
